import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { getGLAccounts } from '../../../../redux/actions/SocietyPortal/GLAccountAction/GLAccountAction';
import { getVendorsDropdown } from '../../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { updateFixedAsset, getFixedAssetDetails, getDepreciationMethods, getFixedAssetCategories } from '../../../../redux/actions/SocietyPortal/FixedAssetAction/FixedAssetAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { toast } from 'react-toastify';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS } from '../../../../Routing/routes';

const MODE_OF_CALCULATION = [
    { value: 'straight', label: 'Straight Method' },
    { value: 'reducing', label: 'Reducing Method' },
];

const MODE_OF_PERIOD = [
    { value: 'day_count', label: 'Day Count Method' },
    { value: 'income_tax', label: 'Income Tax Method' },
];

const emptyDebitLine = () => ({
    id: Date.now() + Math.random(),
    account_cd: '',
    debit: '',
});

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded tw-text-xs focus:tw-outline-none focus:tw-border-blue-500';
const labelClass = 'tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-800 tw-uppercase tw-tracking-wide tw-mb-1 tw-font-semibold';

const EditFixedAssets = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { glAccountsData, loading: glAccountsLoading } = useSelector(state => state.GLAccountReducer);
    const { vendorsDropdownData, vendorsDropdownLoading } = useSelector(state => state.VendorReducers);
    const { updateLoading, detailsLoading, fixedAssetDetails } = useSelector(state => state.FixedAssetReducer);

    // --- Form State ---
    const [assetName, setAssetName] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [assetDescription, setAssetDescription] = useState('');
    const [scannedVoucherLink, setScannedVoucherLink] = useState('');
    const [remarks, setRemarks] = useState('');

    // Depreciation
    const [rateOfDepreciation, setRateOfDepreciation] = useState('');
    const [scrapValue, setScrapValue] = useState('');
    const [modeOfCalculation, setModeOfCalculation] = useState('straight');
    const [modeOfPeriod, setModeOfPeriod] = useState('day_count');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [vendorName, setVendorName] = useState('all');

    // Journal Entry - Debit side
    const [debitLines, setDebitLines] = useState([emptyDebitLine()]);
    const [sgstAccount, setSgstAccount] = useState('all');
    const [sgstDebit, setSgstDebit] = useState('');
    const [cgstAccount, setCgstAccount] = useState('all');
    const [cgstDebit, setCgstDebit] = useState('');
    const [igstAccount, setIgstAccount] = useState('all');
    const [igstDebit, setIgstDebit] = useState('');

    // Journal Entry - Credit side
    const [creditAccount, setCreditAccount] = useState('all');
    const [creditAmount, setCreditAmount] = useState('');
    const [tdsAccount, setTdsAccount] = useState('all');
    const [tdsCredit, setTdsCredit] = useState('');

    // Fetch dropdown data and asset details
    useEffect(() => {
        if (societyId) {
            dispatch(getGLAccounts({ societyId }));
            dispatch(getVendorsDropdown({ societyId }));
            dispatch(getDepreciationMethods());
            dispatch(getFixedAssetCategories({ societyId }));
            dispatch(getFixedAssetDetails({ societyId, id }));
        }
    }, [dispatch, societyId, id]);

    // Populate form from fetched details
    useEffect(() => {
        if (!fixedAssetDetails) return;
        const d = fixedAssetDetails;
        setAssetName(d.name || '');
        setAssetDescription(d.description || '');
        setPurchaseDate(d.acquisition_date || '');

        // Purchase (uses purchases[0])
        const purchase = d.purchases?.[0];
        if (purchase) {
            setPurchaseDate(purchase.purchase_date || d.acquisition_date || '');
            setInvoiceNumber(purchase.invoice_number || '');
            setScannedVoucherLink(purchase.voucher_url || '');
            setRemarks(purchase.remarks || '');
            if (purchase.vendor_id) setVendorName(String(purchase.vendor_id));
        }

        // Depreciation
        if (d.depreciationConfig) {
            const dep = d.depreciationConfig;
            setRateOfDepreciation(dep.rate ? String(parseFloat(dep.rate)) : '');
            setScrapValue(dep.scrap_value ? String(parseFloat(dep.scrap_value)) : '');
            if (dep.method?.code === 'SLM' || dep.method?.id === 1) {
                setModeOfCalculation('straight');
            } else {
                setModeOfCalculation('reducing');
            }
            setModeOfPeriod(dep.period_method === 'day_count' ? 'day_count' : 'income_tax');
        }

        // Journal (uses journals[0].debits[] and journals[0].credits[])
        const journal = d.journals?.[0];
        if (journal) {
            // Populate debit lines from journal debits
            const journalDebits = journal.debits || [];
            if (journalDebits.length > 0) {
                const mainDebits = [];
                journalDebits.forEach(db => {
                    const acCode = db.glAccount?.ac_code || '';
                    const acName = db.glAccount?.name?.toUpperCase() || '';
                    const amt = String(parseFloat(db.amount) || 0);

                    if (acName.includes('SGST')) {
                        setSgstAccount(acCode);
                        setSgstDebit(amt);
                    } else if (acName.includes('CGST')) {
                        setCgstAccount(acCode);
                        setCgstDebit(amt);
                    } else if (acName.includes('IGST')) {
                        setIgstAccount(acCode);
                        setIgstDebit(amt);
                    } else {
                        mainDebits.push({
                            id: db.id || Date.now() + Math.random(),
                            account_cd: acCode,
                            debit: amt,
                        });
                    }
                });
                if (mainDebits.length > 0) setDebitLines(mainDebits);
            }

            // Populate credit lines from journal credits
            const journalCredits = journal.credits || [];
            if (journalCredits.length > 0) {
                journalCredits.forEach(cr => {
                    const acCode = cr.glAccount?.ac_code || '';
                    const acName = cr.glAccount?.name?.toUpperCase() || '';
                    const amt = String(parseFloat(cr.amount) || 0);

                    if (acName.includes('TDS')) {
                        setTdsAccount(acCode);
                        setTdsCredit(amt);
                    } else {
                        setCreditAccount(acCode);
                        setCreditAmount(amt);
                    }
                });
            }
        }
    }, [fixedAssetDetails]);

    // Account options for dropdowns (value = ac_code, but we store id too)
    const accountOptions = useMemo(() => {
        if (!glAccountsData) return [];
        return (glAccountsData || []).map(acc => ({
            value: acc.ac_code,
            label: `${acc.ac_code} - ${acc.name}`,
            id: acc.id,
        }));
    }, [glAccountsData]);

    // Vendor options
    const vendorOptions = useMemo(() => {
        if (!vendorsDropdownData) return [];
        const list = Array.isArray(vendorsDropdownData) ? vendorsDropdownData : [];
        return list.map(v => ({
            value: String(v.vendor_id),
            label: v.business_name,
        }));
    }, [vendorsDropdownData]);

    // Totals
    const totals = useMemo(() => {
        let totalDebit = 0;
        debitLines.forEach(l => { totalDebit += parseFloat(l.debit) || 0; });
        totalDebit += parseFloat(sgstDebit) || 0;
        totalDebit += parseFloat(cgstDebit) || 0;
        totalDebit += parseFloat(igstDebit) || 0;

        let totalCredit = 0;
        totalCredit += parseFloat(creditAmount) || 0;
        totalCredit += parseFloat(tdsCredit) || 0;

        return { totalDebit, totalCredit, isBalanced: totalDebit === totalCredit && totalDebit > 0 };
    }, [debitLines, sgstDebit, cgstDebit, igstDebit, creditAmount, tdsCredit]);

    const purchaseAmount = totals.totalDebit;

    const formatCurrency = (amount) =>
        parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleDebitLineChange = (index, field, value) => {
        setDebitLines(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleAddDebitLine = () => setDebitLines(prev => [...prev, emptyDebitLine()]);

    const handleRemoveDebitLine = (index) => {
        if (debitLines.length <= 1) return;
        setDebitLines(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (!assetName.trim()) {
            toast.warn('Asset name is required');
            return;
        }
        if (!totals.isBalanced) {
            toast.warn('Debit and Credit totals must be balanced');
            return;
        }

        const findAccountId = (acCode) => accountOptions.find(a => a.value === acCode)?.id || undefined;

        const debits = [];
        debitLines.forEach(l => {
            const amt = parseFloat(l.debit) || 0;
            if (amt > 0 && l.account_cd && l.account_cd !== 'all') {
                debits.push({ account_gl_id: findAccountId(l.account_cd), amount: amt });
            }
        });
        if ((parseFloat(sgstDebit) || 0) > 0 && sgstAccount !== 'all') {
            debits.push({ account_gl_id: findAccountId(sgstAccount), amount: parseFloat(sgstDebit) });
        }
        if ((parseFloat(cgstDebit) || 0) > 0 && cgstAccount !== 'all') {
            debits.push({ account_gl_id: findAccountId(cgstAccount), amount: parseFloat(cgstDebit) });
        }
        if ((parseFloat(igstDebit) || 0) > 0 && igstAccount !== 'all') {
            debits.push({ account_gl_id: findAccountId(igstAccount), amount: parseFloat(igstDebit) });
        }

        const credits = [];
        if ((parseFloat(creditAmount) || 0) > 0 && creditAccount !== 'all') {
            credits.push({ account_gl_id: findAccountId(creditAccount), amount: parseFloat(creditAmount) });
        }
        if ((parseFloat(tdsCredit) || 0) > 0 && tdsAccount !== 'all') {
            credits.push({ account_gl_id: findAccountId(tdsAccount), amount: parseFloat(tdsCredit) });
        }

        const taxAmount = (parseFloat(sgstDebit) || 0) + (parseFloat(cgstDebit) || 0) + (parseFloat(igstDebit) || 0);
        const baseAmount = purchaseAmount - taxAmount;

        const payload = {
            societyId,
            id,
            name: assetName.trim(),
            description: assetDescription.trim() || undefined,
            acquisition_date: purchaseDate,
            purchase: {
                vendor_id: vendorName !== 'all' ? parseInt(vendorName) : undefined,
                purchase_date: purchaseDate,
                invoice_number: invoiceNumber || undefined,
                purchase_amount: baseAmount,
                tax_amount: taxAmount,
                total_amount: purchaseAmount,
                voucher_url: scannedVoucherLink || undefined,
                remarks: remarks || undefined,
            },
            depreciation: {
                method_id: modeOfCalculation === 'straight' ? 1 : 2,
                rate: parseFloat(rateOfDepreciation) || undefined,
                scrap_value: parseFloat(scrapValue) || 0,
                period_method: modeOfPeriod === 'day_count' ? 'day_count' : 'yearly',
                start_date: purchaseDate,
            },
            journal: {
                debits,
                credits,
                narration: assetDescription.trim() || `Purchase of ${assetName.trim()}`,
            },
            onSuccess: () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS),
        };

        dispatch(updateFixedAsset(payload));
    };

    const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS);

    if (detailsLoading) {
        return (
            <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen tw-flex tw-items-center tw-justify-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-blue-500"></div>
                    <span className="tw-text-xs tw-text-gray-400">Loading asset details...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-1.5">
                    <button onClick={goBack} className="tw-p-1 tw-rounded hover:tw-bg-gray-200 tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:arrow-left" className="tw-text-sm" />
                    </button>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-gradient-to-br tw-from-indigo-500 tw-to-purple-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:building-06" className="tw-text-white tw-text-xs" />
                        </div>
                        <span className="tw-text-sm tw-font-semibold tw-text-gray-800">Edit Fixed Asset</span>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button onClick={goBack} className="tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                        Back
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={updateLoading || !totals.isBalanced || !assetName.trim()}
                        className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        {updateLoading ? (
                            <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                        ) : (
                            <Icon icon="mdi:check" className="tw-text-xs" />
                        )}
                        Update Asset
                    </button>
                </div>
            </div>

            {/* ---- Purchase Details ---- */}
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-mb-3 tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
                    <div>
                        <label className={labelClass}><Icon icon="mdi:label-outline" className="tw-text-xs" /> Asset Name <span className="tw-text-red-500">*</span></label>
                        <input type="text" value={assetName} onChange={(e) => setAssetName(e.target.value)} placeholder="Enter asset name" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:calendar" className="tw-text-xs" /> Purchase Date <span className="tw-text-red-500">*</span></label>
                        <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:currency-inr" className="tw-text-xs" /> Purchase Amount</label>
                        <input type="text" value={purchaseAmount > 0 ? formatCurrency(purchaseAmount) : ''} readOnly placeholder="Auto-filled with Debit Amount" className={`${inputClass} tw-bg-gray-50 tw-text-gray-500`} />
                    </div>
                    <div className="lg:tw-col-span-3">
                        <label className={labelClass}><Icon icon="mdi:text-box-outline" className="tw-text-xs" /> Asset Description</label>
                        <textarea value={assetDescription} onChange={(e) => setAssetDescription(e.target.value)} placeholder="Enter asset description" rows={2} className={`${inputClass} tw-resize-none`} />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:link-variant" className="tw-text-xs" /> Scanned Voucher Link</label>
                        <input type="text" value={scannedVoucherLink} onChange={(e) => setScannedVoucherLink(e.target.value)} placeholder="Enter voucher link" className={inputClass} />
                    </div>
                </div>
            </div>

            {/* ---- Depreciation ---- */}
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-mb-3 tw-shadow-sm tw-border tw-border-gray-100">
                <h2 className="tw-text-xs tw-font-semibold tw-text-indigo-600 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                    <Icon icon="mdi:chart-timeline-variant-shimmer" className="tw-text-sm" /> Depreciation
                </h2>
                <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                    <div>
                        <label className={labelClass}><Icon icon="mdi:percent" className="tw-text-xs" /> Rate of Depreciation</label>
                        <div className="tw-relative">
                            <input type="number" value={rateOfDepreciation} onChange={(e) => setRateOfDepreciation(e.target.value)} placeholder="0" min="0" max="100" step="0.01" className={`${inputClass} tw-pr-7`} />
                            <span className="tw-absolute tw-right-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-xs tw-text-gray-400">%</span>
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:currency-inr" className="tw-text-xs" /> Scrap Value</label>
                        <input type="number" value={scrapValue} onChange={(e) => setScrapValue(e.target.value)} placeholder="0.00" min="0" step="0.01" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:calculator-variant" className="tw-text-xs" /> Mode of Calculation</label>
                        <SearchableDropdown value={modeOfCalculation} onChange={setModeOfCalculation} options={MODE_OF_CALCULATION} placeholder="Select mode" clearable={false} small />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:calendar-range" className="tw-text-xs" /> Mode of Period</label>
                        <SearchableDropdown value={modeOfPeriod} onChange={setModeOfPeriod} options={MODE_OF_PERIOD} placeholder="Select mode" clearable={false} small />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:file-document-outline" className="tw-text-xs" /> Invoice Number</label>
                        <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Enter invoice number" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}><Icon icon="mdi:store-outline" className="tw-text-xs" /> Vendor Name</label>
                        <SearchableDropdown value={vendorName} onChange={setVendorName} options={vendorOptions} placeholder="Select vendor" loading={vendorsDropdownLoading} small />
                    </div>
                </div>
            </div>

            {/* ---- Journal Entry ---- */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-xs tw-font-semibold tw-text-indigo-600 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:book-open-page-variant-outline" className="tw-text-sm" /> Journal Entry
                    </h2>
                </div>

                {/* For Debit */}
                <div className="tw-px-3 tw-pt-3 tw-pb-1">
                    <h3 className="tw-text-[10px] tw-font-semibold tw-text-blue-600 tw-uppercase tw-tracking-wide tw-mb-2">For Debit</h3>
                    <table className="tw-w-full tw-text-xs tw-mb-2">
                        <thead>
                            <tr className="tw-bg-gray-50">
                                <th className="tw-text-left tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Debit to Account <span className="tw-text-red-500">*</span></th>
                                <th className="tw-text-right tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-w-[140px]">Debit (₹)</th>
                                <th className="tw-w-[36px]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {debitLines.map((line, index) => (
                                <tr key={line.id} className="tw-border-b tw-border-gray-50">
                                    <td className="tw-px-3 tw-py-1.5">
                                        <SearchableDropdown
                                            value={line.account_cd}
                                            onChange={(val) => handleDebitLineChange(index, 'account_cd', val)}
                                            options={accountOptions}
                                            placeholder="Select account"
                                            loading={glAccountsLoading}
                                            clearable={false}
                                            small
                                        />
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <input type="number" value={line.debit} onChange={(e) => handleDebitLineChange(index, 'debit', e.target.value)} placeholder="0.00" min="0" step="0.01" className={`${inputClass} tw-text-right`} />
                                    </td>
                                    <td className="tw-px-1 tw-py-1.5 tw-text-center">
                                        <button onClick={() => handleRemoveDebitLine(index)} disabled={debitLines.length <= 1} className="tw-p-1 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed">
                                            <Icon icon="mdi:trash-can-outline" className="tw-text-xs" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={handleAddDebitLine} className="tw-flex tw-items-center tw-gap-1 tw-text-blue-600 tw-text-xs tw-font-medium hover:tw-text-blue-700 tw-mb-3">
                        <Icon icon="mdi:plus-circle-outline" className="tw-text-sm" />
                        Add More
                    </button>

                    {/* Tax Accounts - Debit Side */}
                    <table className="tw-w-full tw-text-xs">
                        <tbody>
                            <tr className="tw-border-b tw-border-gray-50">
                                <td className="tw-px-3 tw-py-1.5">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">SGST Account</label>
                                    <SearchableDropdown value={sgstAccount} onChange={setSgstAccount} options={accountOptions} placeholder="Select SGST account" loading={glAccountsLoading} small />
                                </td>
                                <td className="tw-px-3 tw-py-1.5 tw-w-[140px]">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">&nbsp;</label>
                                    <input type="number" value={sgstDebit} onChange={(e) => setSgstDebit(e.target.value)} placeholder="0.00" min="0" step="0.01" className={`${inputClass} tw-text-right`} />
                                </td>
                                <td className="tw-w-[36px]"></td>
                            </tr>
                            <tr className="tw-border-b tw-border-gray-50">
                                <td className="tw-px-3 tw-py-1.5">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">CGST Account</label>
                                    <SearchableDropdown value={cgstAccount} onChange={setCgstAccount} options={accountOptions} placeholder="Select CGST account" loading={glAccountsLoading} small />
                                </td>
                                <td className="tw-px-3 tw-py-1.5 tw-w-[140px]">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">&nbsp;</label>
                                    <input type="number" value={cgstDebit} onChange={(e) => setCgstDebit(e.target.value)} placeholder="0.00" min="0" step="0.01" className={`${inputClass} tw-text-right`} />
                                </td>
                                <td className="tw-w-[36px]"></td>
                            </tr>
                            <tr className="tw-border-b tw-border-gray-50">
                                <td className="tw-px-3 tw-py-1.5">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">IGST Account</label>
                                    <SearchableDropdown value={igstAccount} onChange={setIgstAccount} options={accountOptions} placeholder="Select IGST account" loading={glAccountsLoading} small />
                                </td>
                                <td className="tw-px-3 tw-py-1.5 tw-w-[140px]">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">&nbsp;</label>
                                    <input type="number" value={igstDebit} onChange={(e) => setIgstDebit(e.target.value)} placeholder="0.00" min="0" step="0.01" className={`${inputClass} tw-text-right`} />
                                </td>
                                <td className="tw-w-[36px]"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Divider */}
                <div className="tw-border-t tw-border-gray-100"></div>

                {/* For Credit */}
                <div className="tw-px-3 tw-pt-3 tw-pb-1">
                    <h3 className="tw-text-[10px] tw-font-semibold tw-text-red-500 tw-uppercase tw-tracking-wide tw-mb-2">For Credit</h3>
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50">
                                <th className="tw-text-left tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Credit to Account <span className="tw-text-red-500">*</span></th>
                                <th className="tw-text-right tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-w-[140px]">Credit (₹)</th>
                                <th className="tw-w-[36px]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="tw-border-b tw-border-gray-50">
                                <td className="tw-px-3 tw-py-1.5">
                                    <SearchableDropdown value={creditAccount} onChange={setCreditAccount} options={accountOptions} placeholder="Select account" loading={glAccountsLoading} clearable={false} small />
                                </td>
                                <td className="tw-px-3 tw-py-1.5">
                                    <input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="0.00" min="0" step="0.01" className={`${inputClass} tw-text-right`} />
                                </td>
                                <td className="tw-w-[36px]"></td>
                            </tr>
                            <tr className="tw-border-b tw-border-gray-50">
                                <td className="tw-px-3 tw-py-1.5">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">TDS Account</label>
                                    <SearchableDropdown value={tdsAccount} onChange={setTdsAccount} options={accountOptions} placeholder="Select TDS account" loading={glAccountsLoading} small />
                                </td>
                                <td className="tw-px-3 tw-py-1.5">
                                    <label className="tw-text-[10px] tw-text-gray-500 tw-font-medium">&nbsp;</label>
                                    <input type="number" value={tdsCredit} onChange={(e) => setTdsCredit(e.target.value)} placeholder="0.00" min="0" step="0.01" className={`${inputClass} tw-text-right`} />
                                </td>
                                <td className="tw-w-[36px]"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div className="tw-border-t tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                        <div className="tw-flex tw-items-center tw-gap-6">
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Debit</p>
                                <p className="tw-text-sm tw-font-semibold tw-text-emerald-600 tw-mt-0.5">₹{formatCurrency(totals.totalDebit)}</p>
                            </div>
                            <div className="tw-w-px tw-h-8 tw-bg-gray-200"></div>
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Credit</p>
                                <p className="tw-text-sm tw-font-semibold tw-text-red-600 tw-mt-0.5">₹{formatCurrency(totals.totalCredit)}</p>
                            </div>
                        </div>
                        {totals.isBalanced ? (
                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-emerald-50 tw-text-emerald-700 tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
                                <Icon icon="mdi:check-circle" className="tw-text-xs" /> Balanced
                            </span>
                        ) : totals.totalDebit > 0 || totals.totalCredit > 0 ? (
                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-red-50 tw-text-red-600 tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
                                <Icon icon="mdi:alert-circle" className="tw-text-xs" /> Unbalanced
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditFixedAssets;
